import { useEffect, useRef } from "react";
import type { SoundfontSource, SoundfontEntry } from "@midi-fantom-utils/player-core";
import { SoundFontSettings } from "./SoundFontSettings.js";
import { PianoRollAppearance } from "./PianoRollAppearance.js";
import type { PianoRollAppearance as PianoRollAppearanceValue } from "../config.js";

type SettingsModalProps = {
  open: boolean;
  source: SoundfontSource;
  onSourceChange: (next: SoundfontSource) => void;
  catalog: ReadonlyArray<SoundfontEntry>;
  pianoRollAppearance: PianoRollAppearanceValue;
  onAppearanceChange: (next: PianoRollAppearanceValue) => void;
  onClose: () => void;
};

export const SettingsModal = ({
  open,
  source,
  onSourceChange,
  catalog,
  pianoRollAppearance,
  onAppearanceChange,
  onClose,
}: SettingsModalProps) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="settings-modal__backdrop"
      onMouseDown={onBackdropMouseDown}
      role="presentation"
    >
      <div
        className="settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-modal-title"
        ref={dialogRef}
      >
        <header className="settings-modal__head">
          <h2 id="settings-modal-title" className="settings-modal__title">
            Настройки
          </h2>
          <button
            type="button"
            className="btn btn--ghost settings-modal__close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ✕
          </button>
        </header>
        <div className="settings-modal__body">
          <SoundFontSettings
            source={source}
            onSourceChange={onSourceChange}
            catalog={catalog}
          />
          <PianoRollAppearance
            value={pianoRollAppearance}
            onChange={onAppearanceChange}
          />
        </div>
        <footer className="settings-modal__foot">
          <button type="button" className="btn" onClick={onClose}>
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  );
};
